import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Star, Check, Phone, MessageCircle } from "lucide-react";
import LeadForm from "@/components/sections/LeadForm";
import { SITE, waLink, telLink } from "@/lib/site-config";
import { useSiteContent } from "@/lib/site-content";

const usps = [
  "Individuelle Betreuung",
  "Alltagstaugliche Systeme",
  "Langfristige Resultate",
  "WhatsApp Support",
];

const Hero = () => {
  const prefersReducedMotion = useReducedMotion();
  const { images } = useSiteContent();
  const headlineWords = [
    { text: "Stärker.", accent: false },
    { text: "Gesünder.", accent: false },
    { text: "Disziplinierter.", accent: true },
  ];
  return (
    <section
      id="hero"
      className="relative hero-radial grain pt-28 md:pt-32 pb-20 md:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background image (subtle) */}
      <div
        className="absolute inset-0 opacity-[0.18] bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${images.hero || SITE.images.hero})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#5C6B49]/70 via-[#5C6B49]/85 to-[#5C6B49]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.14)] bg-black/[0.03] mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E8E5D0] animate-pulse" />
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E5E2D0]">
                Premium Coaching · Rhein-Main
              </span>
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: prefersReducedMotion ? 0 : 0.12,
                    delayChildren: prefersReducedMotion ? 0 : 0.1,
                  },
                },
              }}
              className="font-display font-black uppercase text-[#F3F1E8] text-[clamp(2.25rem,10vw,3rem)] sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter text-balance"
              data-testid="hero-headline"
            >
              {headlineWords.map((w, idx) => (
                <motion.span
                  key={idx}
                  variants={{
                    hidden: prefersReducedMotion
                      ? { opacity: 1, y: 0, filter: "blur(0px)" }
                      : { opacity: 0, y: 14, filter: "blur(6px)" },
                    visible: {
                      opacity: 1,
                      y: 0,
                      filter: "blur(0px)",
                      transition: {
                        duration: prefersReducedMotion ? 0 : 0.55,
                        ease: [0.22, 1, 0.36, 1],
                      },
                    },
                  }}
                  className={`block ${w.accent ? "lime-gradient-text" : ""}`}
                  style={{ willChange: "transform, opacity, filter" }}
                >
                  {w.text}
                </motion.span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-[#C9C6B4] text-base sm:text-lg max-w-xl leading-relaxed"
            >
              Dein Körper ist dein Fundament. Mit Tobi bekommst du ein
              strukturiertes System aus Krafttraining, Ernährung, Regeneration
              und Mindset – individuell auf dein Leben zugeschnitten, mit
              ehrlichem WhatsApp-Support.
            </motion.p>

            {/* USPs */}
            <motion.ul
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 grid grid-cols-2 gap-3 max-w-md"
            >
              {usps.map((u) => (
                <li key={u} className="flex items-center gap-2 text-sm text-[#E5E2D0]">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#E8E5D0]/15 border border-[#E8E5D0]/40">
                    <Check size={12} className="text-[#E8E5D0]" />
                  </span>
                  {u}
                </li>
              ))}
            </motion.ul>

            {/* Trust + CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5"
            >
              <div className="flex items-center gap-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="fill-[#E8E5D0] text-[#E8E5D0]"
                    />
                  ))}
                </div>
                <div className="text-xs text-[#C9C6B4]">
                  <span className="text-[#F3F1E8] font-bold">4.9/5</span> · 120+ Kunden begleitet
                </div>
              </div>

              <div className="hidden sm:block w-px h-8 bg-[rgba(255,255,255,0.2)]" />

              <div className="flex items-center gap-3">
                <a
                  href={telLink()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[rgba(255,255,255,0.2)] hover:bg-black/[0.04] text-sm font-bold text-[#F3F1E8] transition-colors"
                  data-testid="hero-call-btn"
                >
                  <Phone size={14} /> Anrufen
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-sm font-bold text-white transition-colors"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            </motion.div>
          </div>

          {/* RIGHT – Form */}
          <div className="lg:col-span-5">
            <LeadForm />
          </div>
        </div>

        {/* Logo Strip / Trust — Coaching-Säulen (responsive 2-col grid on mobile) */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 sm:mt-20"
          data-testid="coaching-pillars"
        >
          <span className="block sm:hidden text-[#8F8C7A] text-xs uppercase tracking-[0.25em] mb-4">
            Coaching-Säulen
          </span>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[#8F8C7A] text-xs uppercase tracking-[0.25em] sm:flex sm:flex-wrap sm:items-center sm:gap-x-10 sm:gap-y-4">
            <span className="hidden sm:inline">Coaching-Säulen</span>
            <span className="text-[#C9C6B4]">Krafttraining</span>
            <span className="hidden sm:inline text-[rgba(255,255,255,0.28)]">·</span>
            <span className="text-[#C9C6B4]">Ernährung</span>
            <span className="hidden sm:inline text-[rgba(255,255,255,0.28)]">·</span>
            <span className="text-[#C9C6B4]">Regeneration</span>
            <span className="hidden sm:inline text-[rgba(255,255,255,0.28)]">·</span>
            <span className="text-[#C9C6B4]">Mindset</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
